// store/auth-context.js

import { useState, createContext, useCallback } from "react";
import useAxiosPrivate from "../hooks/useAxiosPrivate";

const ADMIN_TYPES = ["admin", "sales", "merchant", "partner"];

export const AuthContext = createContext({
  user: null,
  setUser: () => {},
  accessToken: null,
  setAccessToken: () => {},
  refreshToken: null,
  setRefreshToken: () => {},
  csrftoken: null,
  setCSRFToken: () => {},
  profileLoading: false,
  loading: true,
  setLoading: () => {},
  refreshProfile: async () => {},
  sessionExpired: false, // New state for session expiry
  handleSessionExpiry: () => {}, // New handler for session expiry
  resetSessionExpired: () => {}, // New function to close/reset the modal
});

export function AuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessTokenState] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [csrftoken, setCSRFToken] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false); // State for the modal
  const axiosPrivate = useAxiosPrivate();

  const setAccessToken = useCallback((token) => {
    setAccessTokenState(token);
    if (token) {
      localStorage.setItem("hasSession", "true");
      localStorage.removeItem("isLoggedOut");
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const { data: userData } = await axiosPrivate.get("auth/user/");
      let profileData = null;
      if (userData?.user_type === "contractor") {
        const { data } = await axiosPrivate.get("profiles/contractor/", {
          params: { user_id: userData.id },
        });
        profileData = data;
      } else if (userData?.user_type === "homeowner") {
        profileData = userData.homeowner_profile || {};
      }
      setUser({
        ...userData,
        contractor_profile:
          userData.user_type === "contractor" ? profileData : null,
        homeowner_profile:
          userData.user_type === "homeowner" ? profileData : null,
      });
      localStorage.removeItem("isLoggedOut");
      localStorage.setItem("hasSession", "true");
    } catch (error) {
      console.error("AuthContext: Failed to fetch user profile.", error.response || error.message);
      setUser(null);
      setAccessToken(null);
      localStorage.setItem("isLoggedOut", "true");
      localStorage.removeItem("hasSession");
    } finally {
      setProfileLoading(false);
    }
  }, [axiosPrivate, setAccessToken]);

  /**
   * Handles the session expiry event.
   * Only shows the re-login modal for admin-type users (admin, sales, merchant, partner).
   * Homeowners and contractors are silently logged out — they'll be redirected
   * to the regular login page naturally when they try to access protected content.
   */
  const handleSessionExpiry = useCallback(() => {
    // Only show the session expired modal for admin-type users.
    if (user && ADMIN_TYPES.includes(user.user_type)) {
      setSessionExpired(true);
    }
    // Clear all authentication state regardless of user type.
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setCSRFToken(null);
    localStorage.setItem("isLoggedOut", "true");
    localStorage.removeItem("hasSession");
  }, [user, setAccessToken]);

  /**
   * Resets the session expired flag, allowing the modal to be closed.
   */
  const resetSessionExpired = () => {
    setSessionExpired(false);
  };

  const contextValue = {
    user,
    setUser,
    accessToken,
    setAccessToken,
    refreshToken,
    setRefreshToken,
    csrftoken,
    setCSRFToken,
    profileLoading,
    loading,
    setLoading,
    refreshProfile,
    sessionExpired, // Expose the new state
    handleSessionExpiry, // Expose the new handler
    resetSessionExpired, // Expose the new reset function
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export default AuthContext;
